/**
 * Quarantaine durable des opérations hors-ligne lors d'une révocation de
 * session. Elle utilise une base IndexedDB SÉPARÉE : la purge normale de
 * `champ-erp-offline` ne peut donc pas effacer accidentellement la copie.
 *
 * Les lots ne sont jamais restaurés automatiquement. Ils portent l'identité
 * connue avant révocation et les domaines concernés afin qu'une future
 * procédure de récupération puisse exiger la même identité et les mêmes
 * memberships. Le contenu n'est exporté par aucune API d'écran.
 */

import Dexie, { type EntityTable } from "dexie";

import { offlineDb } from "./db.js";

export type RaisonQuarantaineSession = "session_revoquee" | "cookie_orphelin" | "deconnexion";

interface OperationQuarantaine {
  file: string;
  entree: Record<string, unknown>;
}
export interface LotQuarantaineSession {
  id: string;
  /** Identité relue depuis le cache auth.me AVANT sa purge, si disponible. */
  utilisateurId: string | null;
  domaineIds: string[];
  creeLe: number;
  raison: RaisonQuarantaineSession;
  operations: OperationQuarantaine[];
}

export interface ResultatQuarantaineSession {
  lotId: string | null;
  nombreOperations: number;
}

class QuarantaineSessionDb extends Dexie {
  lots!: EntityTable<LotQuarantaineSession, "id">;

  constructor() {
    super("champ-erp-offline-quarantaine");
    this.version(1).stores({
      lots: "id, utilisateurId, creeLe, *domaineIds",
    });
  }
}

const quarantaineSessionDb = new QuarantaineSessionDb();

async function lireOperationsNonSynchronisees(): Promise<OperationQuarantaine[]> {
  const files = offlineDb.tables.filter((table) =>
    table.schema.indexes.some((index) => index.name === "statut"),
  );
  const parFile = await Promise.all(
    files.map(async (table) => {
      const entrees = (await table
        .filter((entree) => entree.statut !== "envoye")
        .toArray()) as Record<string, unknown>[];
      return entrees.map((entree) => ({ file: table.name, entree }));
    }),
  );
  return parFile.flat();
}

interface DependancesQuarantaine {
  lireOperations: () => Promise<OperationQuarantaine[]>;
  ecrireLot: (lot: LotQuarantaineSession) => Promise<unknown>;
  genererId: () => string;
  maintenant: () => number;
}

const dependancesParDefaut: DependancesQuarantaine = {
  lireOperations: lireOperationsNonSynchronisees,
  ecrireLot: (lot) => quarantaineSessionDb.lots.put(lot),
  genererId: () => crypto.randomUUID(),
  maintenant: () => Date.now(),
};

/**
 * Copie toutes les entrées non confirmées par le serveur dans un lot durable.
 * Le caller ne doit purger la base active QU'APRÈS résolution de cette
 * promesse. Un échec laisse ainsi les outboxes originales intactes.
 */
export async function mettreOperationsEnQuarantaine(
  input: {
    utilisateurId: string | null;
    raison: RaisonQuarantaineSession;
  },
  dependances: DependancesQuarantaine = dependancesParDefaut,
): Promise<ResultatQuarantaineSession> {
  const operations = await dependances.lireOperations();
  if (operations.length === 0) return { lotId: null, nombreOperations: 0 };

  const domaineIds = [
    ...new Set(
      operations
        .map(({ entree }) => entree["domaineId"])
        .filter((id): id is string => typeof id === "string" && id.length > 0),
    ),
  ].sort();
  const lot: LotQuarantaineSession = {
    id: dependances.genererId(),
    utilisateurId: input.utilisateurId,
    domaineIds,
    creeLe: dependances.maintenant(),
    raison: input.raison,
    operations,
  };

  // Un seul put atomique dans la base de quarantaine. Si l'écriture échoue,
  // le caller reçoit l'erreur et ne touche pas à la base active.
  await dependances.ecrireLot(lot);
  return { lotId: lot.id, nombreOperations: operations.length };
}
